/**
 * useTranscript
 *
 * Live speech-to-text transcription using the browser's Web Speech API.
 * Falls back gracefully when the API is unavailable (Firefox, some mobile).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface TranscriptLine {
  id:        string;
  text:      string;
  isFinal:   boolean;
  timestamp: Date;
}

// Grab the constructor from window with a loose type so we don't need
// lib declarations for the Web Speech API.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySpeechRecognition = any;

function getSpeechRecognitionCtor(): AnySpeechRecognition | null {
  if (typeof window === 'undefined') return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useTranscript(lang = 'en-US') {
  const recognitionRef = useRef<AnySpeechRecognition>(null);
  const interimRef     = useRef<string>('');

  const [lines, setLines]           = useState<TranscriptLine[]>([]);
  const [isListening, setListening] = useState(false);

  const SpeechRecognitionCtor = getSpeechRecognitionCtor();
  const supported = Boolean(SpeechRecognitionCtor);

  const start = useCallback(() => {
    if (!SpeechRecognitionCtor || isListening) return;

    const r = new SpeechRecognitionCtor();
    r.lang             = lang;
    r.continuous       = true;
    r.interimResults   = true;
    r.maxAlternatives  = 1;

    r.onstart = () => setListening(true);

    r.onend = () => {
      setListening(false);
      // Auto-restart if still mounted
      if (recognitionRef.current) {
        try { recognitionRef.current.start(); } catch (_) { /* ignore */ }
      }
    };

    r.onerror = (evt: { error: string }) => {
      if (evt.error !== 'no-speech') {
        console.warn('SpeechRecognition error:', evt.error);
        setListening(false);
      }
    };

    r.onresult = (evt: {
      resultIndex: number;
      results: { isFinal: boolean; 0: { transcript: string } }[];
    }) => {
      let interim = '';
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        const result = evt.results[i];
        if (result.isFinal) {
          const text = result[0].transcript.trim();
          if (text) {
            setLines((prev) => [
              ...prev,
              { id: crypto.randomUUID(), text, isFinal: true, timestamp: new Date() },
            ]);
          }
          interim = '';
        } else {
          interim += result[0].transcript;
        }
      }
      interimRef.current = interim;

      setLines((prev) => {
        const finals = prev.filter((l) => l.isFinal);
        if (!interim) return finals;
        return [
          ...finals,
          { id: 'interim', text: interim, isFinal: false, timestamp: new Date() },
        ];
      });
    };

    recognitionRef.current = r;
    try { r.start(); } catch (e) { console.error('SpeechRecognition start failed', e); }
  }, [SpeechRecognitionCtor, isListening, lang]);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setListening(false);
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const fullText = lines.filter((l) => l.isFinal).map((l) => l.text).join(' ');

  useEffect(() => () => stop(), [stop]);

  return { lines, fullText, isListening, supported, start, stop, clear };
}
